import { useState, useEffect } from 'react';

interface SummaryData {
  total_earnings: number;
  jobs_completed: number;
  average_per_job: number;
  commission_paid: number;
}

interface Props {
  timeframe: { from: string; to: string };
}

export default function EarningsSummaryCards({ timeframe }: Props) {
  const [data, setData] = useState<SummaryData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSummary();
  }, [timeframe]);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const token = localStorage.getItem('supabase_token');

      const url = new URL(`${supabaseUrl}/functions/v1/get-provider-earnings-summary`);
      url.searchParams.append('from', timeframe.from);
      url.searchParams.append('to', timeframe.to);

      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-lg p-6 shadow-sm animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-24 mb-3"></div>
            <div className="h-8 bg-gray-200 rounded w-32"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!data) return null;

  const cards = [
    {
      label: 'Your earnings',
      value: `£${data.total_earnings.toFixed(2)}`,
      description: 'Net earnings after platform fee',
      icon: 'ri-money-pound-circle-line',
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
    },
    {
      label: 'Jobs completed',
      value: data.jobs_completed.toString(),
      description: 'Total completed jobs',
      icon: 'ri-checkbox-circle-line',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      label: 'Average per job',
      value: `£${data.average_per_job.toFixed(2)}`,
      description: 'Average earnings per job',
      icon: 'ri-line-chart-line',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      label: 'Platform fee',
      value: `£${data.commission_paid.toFixed(2)}`,
      description: '15% fee applied to completed jobs',
      icon: 'ri-information-line',
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {cards.map((card, index) => (
        <div key={index} className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-3">
            <div className="text-sm font-medium text-gray-600">{card.label}</div>
            <div className={`w-10 h-10 ${card.bgColor} rounded-lg flex items-center justify-center`}>
              <i className={`${card.icon} ${card.color} text-xl`}></i>
            </div>
          </div>
          <div className="text-3xl font-bold text-gray-900 mb-1">{card.value}</div>
          <div className="text-xs text-gray-500">{card.description}</div>
        </div>
      ))}
    </div>
  );
}
